import "./CaseAdminFilters.css";

import { SyntheticEvent, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useLocalStorage } from "react-use";

import { Case, Lab } from "pwapi";

import { fetchAdminCases } from "shared/actions/admin/adminCasesActions";
import { getLabs } from "shared/actions/labActions";
import { FeatureFlags } from "shared/reducers/sysReducer";

import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import { ControlledToggleButton } from "components/customInputs/buttons/ToggleButton";
import {
  reduceFiltersForSearchParams,
  reduceFiltersToApi,
  resetBooleanFilters,
  resetFilters,
} from "components/filtersUtils";
import { useAppSelector } from "store/hooks";

import {
  CaseFilterState,
  DateRangeFiltersState,
  FilterLabels,
  FilterOptions,
  FilterValue,
  QueryFiltersState,
  SlideFiltersState,
  SpecimenFiltersState,
} from "./CaseAdminFilters.contracts";
import { useSanitizeFilterValue } from "./CaseAdminFilters.hooks";
import {
  caseFiltersStateEmpty,
  createQueryFiltersStateInit,
  dateRangeFiltersStateEmpty,
  queryFiltersStateEmpty,
  slideFiltersStateEmpty,
  specimenFiltersStateEmpty,
} from "./CaseAdminFilters.init";
import {
  getCompleteFilterLabel,
  matchCert,
  reduceQueriesToApi,
} from "./CaseAdminFilters.utils";
import Chip from "../Chip";
import ChipList from "../ChipList";
import CaseFilters, {
  mapCaseFilterToLabel,
  qaReviewLabels,
} from "../filters/CaseFilters";
import DateRangeFilters, {
  mapDateRangeFilterToLabel,
} from "../filters/DateRangeFilters";
import HiddenDropDown from "../filters/HiddenDropdown";
import SlidesFilters, {
  mapSlidesFilterToLabel,
} from "../filters/SlidesFilters";
import SpecimenFilters, {
  mapSpecimensFilterToLabel,
} from "../filters/SpecimenFilters";
import { FilterPageFunctionComponentProps } from "../types/adminTypes";

export const mapQueryFilterToOptions: FilterOptions<QueryFiltersState> = {
  isManuallyAccessioned: [
    { label: "Manual", value: "true" },
    { label: "Ordered", value: "false" },
  ],
};

export const mapQueryFilterToLabel: FilterLabels<QueryFiltersState> = {
  isDiagnostic: "Diagnostic Cases",
  reviewed: "Unreviewed Cases",
  dataEntry: "Needing Data Entry",
  billingEntry: "Billing Entry Holds",
  hasFinalizingSignature: "Missing Finalizing Signature",
  unviewed: "Unviewed Cases",
  selfPay: "Self Pay Cases",
  isManuallyAccessioned: "Accession Method",
};

type FilterGroups = {
  case: CaseFilterState;
  dateRange: DateRangeFiltersState;
  slide: SlideFiltersState;
  specimen: SpecimenFiltersState;
  query: QueryFiltersState;
};

const mapFilterGroupToFilterToEmpty: FilterGroups = {
  case: caseFiltersStateEmpty,
  dateRange: dateRangeFiltersStateEmpty,
  slide: slideFiltersStateEmpty,
  specimen: specimenFiltersStateEmpty,
  query: queryFiltersStateEmpty,
};

const mapFilterGroupToFilterToLabel = {
  case: mapCaseFilterToLabel,
  dateRange: mapDateRangeFilterToLabel,
  slide: mapSlidesFilterToLabel,
  specimen: mapSpecimensFilterToLabel,
  query: mapQueryFilterToLabel,
} as const;

type FilterSpec = typeof mapFilterGroupToFilterToLabel;
type GroupKey = keyof FilterSpec;
type FilterKey<G extends GroupKey> = keyof FilterSpec[G] & string;
export type AnyFilterKey = {
  [G in GroupKey]: keyof FilterSpec[G];
}[GroupKey] &
  string;

const stringifyValue = (value: any) =>
  typeof value === "string" ? value : JSON.stringify(value);

// Type guards
function isGroupKey(x: string): x is GroupKey {
  return x in mapFilterGroupToFilterToLabel;
}
function isFilterKey<G extends GroupKey>(
  group: G,
  key: string
): key is FilterKey<G> {
  return key in mapFilterGroupToFilterToLabel[group];
}

// Verify params are type safe, and then perform mapping
export function getFilterLabel(group: string, key: string) {
  if (!isGroupKey(group)) return undefined;
  if (!isFilterKey(group, key)) return undefined;
  return mapFilterGroupToFilterToLabel[group][key];
}

const makeTagKey = (groupFilterKey: any, filterKey: any) =>
  `${groupFilterKey}-${filterKey}`;

const createHydrateFiltersState = (usp: URLSearchParams) => (filters: any) => {
  const f = Object.keys(filters).reduce<Record<string, FilterValue>>(
    (acc, value) => {
      const initialFilter = usp.get(value);
      acc[value] = initialFilter
        ? value === "qaReviewHold"
          ? initialFilter.split(",").map((reviewFilter: string) => ({
              label:
                qaReviewLabels[reviewFilter as keyof typeof qaReviewLabels],
              value: reviewFilter,
            }))
          : initialFilter
        : "";
      return acc;
    },
    {}
  );

  // swap keys to maintain backwards compatibility with bookmarks
  if (f["patientClinicSonicPracticeName"]) {
    if (!f["sonicPracticeName"])
      f["sonicPracticeName"] = f["patientClinicSonicPracticeName"];
    delete f["patientClinicSonicPracticeName"];
  }

  return f;
};

const CaseAdminFilters = ({
  sortBy,
  pageSize,
  pageIndex,
  toggleAllRowsExpanded,
  setPage,
}: FilterPageFunctionComponentProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const features: FeatureFlags = useAppSelector(({ sys }) => sys.features);

  const labsState = useAppSelector((state) => state.labs);
  const labs =
    labsState?.results &&
    [...labsState?.results].sort((labA: Lab, labB: Lab) =>
      labA.name > labB.name ? 1 : -1
    );

  const sanitizeFilterValue = useSanitizeFilterValue(labs);

  useEffect(() => {
    !labsState && dispatch(getLabs({ limit: 9999, offset: 0 }));
  }, []);

  const [initCaseFiltersVisible, setInitCaseFiltersVisible] =
    useLocalStorage<boolean>("adminCaseFiltersVisible", true);
  const [initDateRangeFiltersVisible, setInitDateRangeFiltersVisible] =
    useLocalStorage<boolean>("adminDateRangeFiltersVisible", true);
  const [initSpecimenFiltersVisible, setInitSpecimenFiltersVisible] =
    useLocalStorage<boolean>("adminSpecimenFiltersVisible", true);
  const [initSlideFiltersVisible, setInitSlideFiltersVisible] =
    useLocalStorage<boolean>("adminSlideFiltersVisible", true);

  const usp = new URLSearchParams(location.search);

  const filtersStateInit = useMemo(() => {
    const hydrateFiltersState = createHydrateFiltersState(usp);
    return {
      case: hydrateFiltersState(caseFiltersStateEmpty),
      dateRange: hydrateFiltersState(dateRangeFiltersStateEmpty),
      slide: hydrateFiltersState(slideFiltersStateEmpty),
      specimen: hydrateFiltersState(specimenFiltersStateEmpty),
      query: createQueryFiltersStateInit(usp),
    };
  }, [usp]);

  const activeFilters = useMemo(
    () =>
      Object.entries(filtersStateInit).flatMap(
        ([filterGroupKey, filterGroup]) =>
          Object.entries(filterGroup)
            .filter(([filterKey, keyword]) => {
              // Generally, we want to exclude all falsy values from state

              // WITH THIS EXCEPTION: if `isManuallyAccessioned` is explicitly `false`, keep it.
              const isExplicitFalseForIsManuallyAccessioned =
                filterKey === "isManuallyAccessioned" && keyword === false;
              if (isExplicitFalseForIsManuallyAccessioned) return true;

              // Otherwise proceed with scrapping falsy values.
              return !!keyword;
            })
            .map(([filterKey, keyword]) => ({
              filterGroupKey,
              filterKey,
              keyword,
            }))
      ),
    [filtersStateInit]
  );

  const filtersStateInitCounts = useMemo(() => {
    return activeFilters.reduce((acc: { [key in GroupKey]: number }, curr) => {
      const { filterGroupKey, filterKey } = curr;

      // Special case:  isManuallyAccessioned filter counts toward "case" group
      const expectedKey =
        filterKey === "isManuallyAccessioned"
          ? "case"
          : (filterGroupKey as GroupKey);

      acc[expectedKey] = (acc[expectedKey] || 0) + 1;
      return acc;
    }, {} as { [key in GroupKey]: number });
  }, [activeFilters]);

  const [queryFiltersState, setQueryFiltersState] = useState<any>(
    filtersStateInit.query
  );

  const [slideFiltersState, setSlideFiltersState] = useState<any>(
    filtersStateInit.slide
  );

  const [specimenFiltersState, setSpecimenFiltersState] = useState<any>(
    filtersStateInit.specimen
  );

  const [caseFiltersState, setCaseFiltersState] = useState<any>(
    filtersStateInit.case
  );

  const [dateRangeFiltersState, setDateRangeFiltersState] = useState<any>(
    filtersStateInit.dateRange
  );

  // A variable to reference if the component has just mounted
  // This is intended for use to block the first case list api call on first render.
  const didMount = useRef<boolean>(false);

  useEffect(() => {
    // Do not set the search params if default params are true and the filters have just mounted
    // This is to prevent changing the location object and causing a second render
    if (didMount.current || sortBy[0]?.id !== "priority" || pageIndex !== 0)
      setSearchParams({
        case: caseFiltersState,
        dateRange: dateRangeFiltersState,
        slide: slideFiltersState,
        specimen: specimenFiltersState,
        query: queryFiltersState,
      });
  }, [sortBy, pageIndex]);

  useEffect(() => {
    const filtersAreEmpty = [
      ...Object.values(caseFiltersState),
      ...Object.values(dateRangeFiltersState),
      ...Object.values(slideFiltersState),
      ...Object.values(specimenFiltersState),
    ].every((value) => value === "");

    // Do not run if all the defaults are true and it just mounted for the first time.
    // If the filters are empty, the list is in its default state.
    if (
      !filtersAreEmpty ||
      (sortBy?.length > 0 && sortBy[0].id !== "priority") ||
      pageIndex !== 0 ||
      didMount.current
    ) {
      filterCases({
        case: caseFiltersState,
        dateRange: dateRangeFiltersState,
        slide: slideFiltersState,
        specimen: specimenFiltersState,
        query: queryFiltersState,
      });
    }

    didMount.current = true;
  }, [sortBy, pageIndex, pageSize]);

  const applyFilters = () => {
    setSearchParams(allFilters);
    filterCases(allFilters, true);
    // staleTagKeys will be cleared once location.search updates (see effect)
  };

  const onChangeSlideFilter = (key: string, value: string) => {
    setSlideFiltersState({ ...slideFiltersState, [key]: value });
  };

  const onChangeSpecimenFilter = (key: string, value: string) => {
    setSpecimenFiltersState({ ...specimenFiltersState, [key]: value });
  };

  const onChangeCaseFilter = (key: string, value: string | string[]) => {
    setCaseFiltersState({ ...caseFiltersState, [key]: value });
  };

  const onChangeSpecimenDateRangeFilter = (values: { [key: string]: Date }) => {
    onChangeDateRangeFilter(
      specimenFiltersState,
      setSpecimenFiltersState,
      values
    );
  };

  const onChangeSlideDateRangeFilter = (values: { [key: string]: Date }) => {
    onChangeDateRangeFilter(slideFiltersState, setSlideFiltersState, values);
  };

  const onChangeDateRangeFilters = (values: { [key: string]: Date }) => {
    onChangeDateRangeFilter(
      dateRangeFiltersState,
      setDateRangeFiltersState,
      values
    );
  };

  const onChangeQueryFilter = (key: string) => {
    setQueryFiltersState({
      ...queryFiltersState,
      [key]: !queryFiltersState[key],
    });
  };

  const onChangeQueryFilterControlled = (key: string, value?: boolean) => {
    setQueryFiltersState({ ...queryFiltersState, [key]: value });
  };

  const onChangeDateRangeFilter = (
    filters: string[],
    set: Function,
    values: { [key: string]: Date }
  ) => {
    const newState = Object.assign({}, filters, values);
    set(newState);
  };

  const setSearchParams = (filters: any) => {
    const params = {
      ...reduceFiltersForSearchParams(filters.case),
      ...reduceFiltersForSearchParams(filters.dateRange),
      ...reduceFiltersForSearchParams(filters.slide),
      ...reduceFiltersForSearchParams(filters.specimen),
      ...reduceQueriesToApi(filters.query),
    } as any;

    if (sortBy.length) {
      params.sortBy = sortBy.map((s) => `${s.desc ? "-" : ""}${s.id}`);
    }
    if (pageIndex > 0) {
      params.page = pageIndex + 1;
    }
    navigate(`${location.pathname}?${new URLSearchParams(params).toString()}`, {
      replace: true,
    });
  };

  const filterCases = (filters: FilterGroups, resetPage?: boolean) => {
    const grossedByCertFilter = matchCert(filters.case.grossedByCert);
    const source =
      filters.query.reviewed || filters.query.hasFinalizingSignature
        ? Case.source.PW
        : filters.case.source;

    const payload = {
      limit: pageSize,
      offset: resetPage ? 0 : pageSize * pageIndex,
      ordering: [
        ...sortBy.map((s) => {
          const descending = s.desc ? "-" : "";
          return s.id === "priority"
            ? "-priority,-patient__clinic__tat_priority,received_date"
            : `${descending}${s.id}`;
        }),
      ].join(","),
      ...reduceFiltersToApi(filters.case),
      ...reduceFiltersToApi(filters.dateRange),
      ...reduceFiltersToApi(filters.slide),
      ...reduceFiltersToApi(filters.specimen),
      ...reduceQueriesToApi(filters.query),
      ...(grossedByCertFilter && { grossedByCert: grossedByCertFilter }),
      ...(source && { source: source }),
    };
    toggleAllRowsExpanded && toggleAllRowsExpanded(false);
    resetPage && pageIndex !== 0
      ? setPage(0)
      : dispatch(
          fetchAdminCases({
            filters: payload,
            isManualReview: filters.slide?.slidesStatus === "manual-review",
          })
        );
  };

  const allFilters = useMemo<FilterGroups>(
    () => ({
      case: caseFiltersState,
      dateRange: dateRangeFiltersState,
      slide: slideFiltersState,
      specimen: specimenFiltersState,
      query: queryFiltersState,
    }),
    [
      caseFiltersState,
      dateRangeFiltersState,
      slideFiltersState,
      specimenFiltersState,
      queryFiltersState,
    ]
  );

  // Defer applying filters until after the relevant state has committed.
  const refApplyFiltersAfterCommit = useRef<boolean>(false);
  useEffect(() => {
    if (refApplyFiltersAfterCommit.current) {
      refApplyFiltersAfterCommit.current = false;
      applyFilters();
    }
  }, [allFilters]);

  const onSubmitFilter = (e: SyntheticEvent) => {
    e.preventDefault();
    applyFilters();
  };

  const onResetFilter = () => {
    const filters = {
      case: resetFilters(caseFiltersState),
      dateRange: resetFilters(dateRangeFiltersState),
      slide: resetFilters(slideFiltersState),
      specimen: resetFilters(specimenFiltersState),
      query: resetBooleanFilters(queryFiltersState),
    };
    setSlideFiltersState(filters.slide);
    setCaseFiltersState(filters.case);
    setDateRangeFiltersState(filters.dateRange);
    setSpecimenFiltersState(filters.specimen);
    setQueryFiltersState(filters.query);
    setSearchParams(filters);

    refApplyFiltersAfterCommit.current = true;
  };

  const mapFilterGroupToStateSetter = {
    case: setCaseFiltersState,
    dateRange: setDateRangeFiltersState,
    slide: setSlideFiltersState,
    specimen: setSpecimenFiltersState,
    query: setQueryFiltersState,
  };

  const handleRequestClose =
    (filterGroupKey: string, filterKey: string) => () => {
      if (!isGroupKey(filterGroupKey)) return;
      if (!isFilterKey(filterGroupKey, filterKey)) return;

      const setFilterState = mapFilterGroupToStateSetter[filterGroupKey];
      setFilterState((prev: any) => ({
        ...prev,
        [filterKey]: mapFilterGroupToFilterToEmpty[filterGroupKey][filterKey],
      }));

      // Apply after state commits so allFilters contains the cleared key
      refApplyFiltersAfterCommit.current = true;
    };

  const activeFilterList = useMemo(() => {
    return (
      !!activeFilters.length && (
        <ChipList className="AdminFilters__filters">
          {activeFilters.map(({ filterGroupKey, filterKey, keyword }) => (
            <Chip
              className={`AdminFilters__filter AdminFilters__filter--${filterGroupKey}`}
              key={makeTagKey(filterGroupKey, filterKey)}
              onRequestClose={handleRequestClose(filterGroupKey, filterKey)}
              isClosable
            >
              <strong>{getFilterLabel(filterGroupKey, filterKey)}</strong>:{" "}
              {sanitizeFilterValue(
                filterKey as AnyFilterKey,
                stringifyValue(keyword)
              )}
            </Chip>
          ))}
        </ChipList>
      )
    );
  }, [activeFilters]);

  return (
    <div className="AdminFilters">
      <form className="AdminFilters__Form" onSubmit={onSubmitFilter}>
        <div className="AdminFilters__Form--interior">
          <div className="AdminFilters__Form AdminFilters--vertical">
            <HiddenDropDown
              label={getCompleteFilterLabel(
                "Case Filters",
                filtersStateInitCounts.case
              )}
              type="filter"
              isInitOpen={initCaseFiltersVisible}
              setLocalStorage={setInitCaseFiltersVisible}
            >
              <div className="AdminFilters__filters">
                <CaseFilters
                  {...caseFiltersState}
                  labs={labs}
                  isManuallyAccessioned={
                    queryFiltersState.isManuallyAccessioned
                  }
                  onChange={onChangeCaseFilter}
                  onChangeManuallyAccessioned={onChangeQueryFilterControlled}
                />
              </div>
            </HiddenDropDown>
            <HiddenDropDown
              label={getCompleteFilterLabel(
                "Case Date Filters",
                filtersStateInitCounts.dateRange
              )}
              type="filter"
              isInitOpen={initDateRangeFiltersVisible}
              setLocalStorage={setInitDateRangeFiltersVisible}
            >
              <div className="AdminFilters__filters">
                <DateRangeFilters
                  {...dateRangeFiltersState}
                  onChangeDateRange={onChangeDateRangeFilters}
                />
              </div>
            </HiddenDropDown>
            <HiddenDropDown
              label={getCompleteFilterLabel(
                "Specimen Filters",
                filtersStateInitCounts.specimen
              )}
              type="filter"
              isInitOpen={initSpecimenFiltersVisible}
              setLocalStorage={setInitSpecimenFiltersVisible}
            >
              <div className="AdminFilters__filters">
                <SpecimenFilters
                  {...specimenFiltersState}
                  onChange={onChangeSpecimenFilter}
                  onChangeDateRange={onChangeSpecimenDateRangeFilter}
                />
              </div>
            </HiddenDropDown>
            <HiddenDropDown
              label={getCompleteFilterLabel(
                "Slide Filters",
                filtersStateInitCounts.slide
              )}
              type="filter"
              isInitOpen={initSlideFiltersVisible}
              setLocalStorage={setInitSlideFiltersVisible}
            >
              <div className="AdminFilters__filters">
                <SlidesFilters
                  {...slideFiltersState}
                  labs={labs}
                  onChange={onChangeSlideFilter}
                  onChangeDateRange={onChangeSlideDateRangeFilter}
                />
              </div>
            </HiddenDropDown>
          </div>
          <div className="AdminFilters__actions AdminFilters__actions--content">
            <div className="AdminFilters__actions--content">
              <ControlledToggleButton
                isToggled={!!queryFiltersState.isDiagnostic}
                onClick={() => {
                  onChangeQueryFilter("isDiagnostic");
                }}
              >
                {mapQueryFilterToLabel["isDiagnostic"]}
              </ControlledToggleButton>
              {features?.qaReviews && (
                <ControlledToggleButton
                  isToggled={!!queryFiltersState.reviewed}
                  onClick={() => {
                    setQueryFiltersState({
                      ...queryFiltersState,
                      dataEntry: false,
                      reviewed: !queryFiltersState.reviewed,
                      billingEntry: undefined,
                    });
                  }}
                >
                  {mapQueryFilterToLabel["reviewed"]}
                </ControlledToggleButton>
              )}
              <ControlledToggleButton
                isToggled={!!queryFiltersState.dataEntry}
                onClick={() => {
                  setQueryFiltersState({
                    ...queryFiltersState,
                    dataEntry: !queryFiltersState.dataEntry,
                    reviewed: undefined,
                    billingEntry: undefined,
                  });
                }}
              >
                {mapQueryFilterToLabel["dataEntry"]}
              </ControlledToggleButton>
              {features?.billingEntry && (
                <>
                  <ControlledToggleButton
                    isToggled={queryFiltersState.billingEntry === true}
                    onClick={() => {
                      setQueryFiltersState({
                        ...queryFiltersState,
                        dataEntry: undefined,
                        reviewed: undefined,
                        billingEntry: queryFiltersState.billingEntry
                          ? undefined
                          : true,
                      });
                    }}
                  >
                    {mapQueryFilterToLabel["billingEntry"]}
                  </ControlledToggleButton>
                  <ControlledToggleButton
                    isToggled={queryFiltersState.billingEntry === false}
                    onClick={() => {
                      setQueryFiltersState({
                        ...queryFiltersState,
                        dataEntry: undefined,
                        reviewed: undefined,
                        billingEntry: false,
                      });
                    }}
                  >
                    {mapQueryFilterToLabel["billingEntry"]}
                  </ControlledToggleButton>
                </>
              )}
              <ControlledToggleButton
                isToggled={!!queryFiltersState.hasFinalizingSignature}
                onClick={() => {
                  onChangeQueryFilter("hasFinalizingSignature");
                }}
              >
                {mapQueryFilterToLabel["hasFinalizingSignature"]}
              </ControlledToggleButton>
              <ControlledToggleButton
                isToggled={!!queryFiltersState.unviewed}
                onClick={() => {
                  onChangeQueryFilter("unviewed");
                }}
              >
                {mapQueryFilterToLabel["unviewed"]}
              </ControlledToggleButton>
              <ControlledToggleButton
                isToggled={!!queryFiltersState.selfPay}
                onClick={() => {
                  onChangeQueryFilter("selfPay");
                }}
              >
                {mapQueryFilterToLabel["selfPay"]}
              </ControlledToggleButton>
            </div>
            <div className="AdminFilters__actions--content">
              <PathologyWatchButton
                type="submit"
                color="green"
                title="filter cases"
              >
                Filter Cases
              </PathologyWatchButton>
              <PathologyWatchButton
                onClick={onResetFilter}
                title="reset filters"
              >
                Reset Filters
              </PathologyWatchButton>
            </div>
          </div>
        </div>
        <div className="AdminFilters__Form--interior">{activeFilterList}</div>
      </form>
    </div>
  );
};

export default CaseAdminFilters;
